package DataBase

import Network.NetworkOutput
import Network.httpWorker
import Utils.getJsonWithConfig
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive

/**
 * Mints a single-use client self-signup / edit-card pass (method 5) and returns
 * the public v2 URL: `https://nihulkav.shop/v2/clientsign?pass=...`.
 *
 * Gated by cofc_conf.rep_link bit 2.
 */
object ClientSignLinksClient {

    private const val VIEWER_BASE = "${Structs.CofcConf.ORDER_SHOP_HOST}/v2/clientsign"

    data class Result(
        val url: String? = null,
        val ok: Boolean = false,
        val error: String? = null,
    )

    /** cofc_conf.rep_link bit 2 — client self-signup / edit docs feature. */
    fun isEnabled(): Boolean {
        val conf = try {
            UINKDBInterface.activeDB.getUserOrderCofc().first
        } catch (_: Exception) {
            null
        }
        return ((conf?.rep_link ?: 0) and 2) != 0
    }

    suspend fun createEditLink(clientId: Int): Result {
        if (clientId <= 0) return Result(error = "לקוח לא תקין")
        return mintLink(clientId)
    }

    suspend fun createSignupLink(): Result = mintLink(clientId = null)

    private suspend fun mintLink(clientId: Int?): Result {
        val company = httpWorker.companyName
        val companyId = httpWorker.companyId
        val token = httpWorker.curToken
        if (company.isBlank() || companyId.isBlank() || token.isBlank()) {
            return Result(error = "אין חיבור לשרת — התחבר מחדש")
        }
        val body = buildJsonObject {
            put("company_id", JsonPrimitive(companyId))
            put("company", JsonPrimitive(company))
            if (clientId != null && clientId > 0) {
                put("client_id", JsonPrimitive(clientId.toString()))
            }
        }.toString()
        val res = httpWorker.purePostApi(
            url = "$server_ip2:$port/clientsign/register",
            params = null,
            token = token,
            jsonStr = body,
        )
        if (res.second != NetworkOutput.SUCCESS || res.first.isNullOrBlank()) {
            return Result(error = extractError(res.first) ?: "יצירת לינק נכשלה")
        }
        return try {
            val pass = unwrapPass(res.first!!)
            if (pass.isBlank()) Result(error = "יצירת לינק נכשלה")
            else Result(url = "$VIEWER_BASE?pass=${encodeUrl(pass)}", ok = true)
        } catch (e: IllegalStateException) {
            Result(error = e.message ?: "יצירת לינק נכשלה")
        }
    }

    private fun extractError(raw: String?): String? {
        if (raw.isNullOrBlank()) return null
        return try {
            val root = getJsonWithConfig().parseToJsonElement(raw) as? JsonObject ?: return raw
            root["status_msg"]?.jsonPrimitive?.contentOrNull
                ?: root["msg"]?.jsonPrimitive?.contentOrNull
                ?: raw
        } catch (_: Exception) {
            raw
        }
    }

    private fun unwrapPass(raw: String): String {
        val root = getJsonWithConfig().parseToJsonElement(raw) as? JsonObject
            ?: return raw.trim().trim('"')
        val status = root["status"]?.jsonPrimitive?.contentOrNull
        if (status != null && status != "SUCCESS") {
            error(root["status_msg"]?.jsonPrimitive?.contentOrNull ?: "request failed")
        }
        val msg = root["status_msg"]?.jsonPrimitive?.contentOrNull
            ?: root["data"]?.jsonPrimitive?.contentOrNull
        return msg?.trim()?.trim('"') ?: raw.trim().trim('"')
    }

    private fun encodeUrl(value: String): String =
        value.encodeToByteArray().joinToString("") { b ->
            val c = b.toInt() and 0xFF
            when {
                c in 0x30..0x39 || c in 0x41..0x5A || c in 0x61..0x7A ||
                    c == 0x2D || c == 0x5F || c == 0x2E || c == 0x7E ->
                    c.toChar().toString()
                else -> "%${c.toString(16).uppercase().padStart(2, '0')}"
            }
        }
}
